import {
    CreateExtArgs,
    DalValue,
    DalSchema,
    DmApis,
    Extension,
    ExtensionAPI,
    ValidatorResult,
    pointerUtils
} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import {namespaceMapping} from '@wix/document-services-json-schemas'
import type {
    ComponentSchemasDefinition,
    Pointer,
    RefInfo,
    ResolvedReference,
    SchemaFile
} from '@wix/document-services-types'
import _ from 'lodash'
import {namespacesWithoutTypeAndId} from './namespacesWithoutTypeAndId'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, VIEW_MODES} from '../../constants/constants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import type {DataAccessExtensionApi} from '../dataAccess/dataAccess'

export interface SchemaValidationError {
    message: string
    dataPath: string
    keyword: string
    schemaPath: string
    params: {
        additionalProperty: string
    }
}

export interface ParsedSchemaValidationErrorMessage {
    offendedDataId: string
    offendedDataNamespace: string
    errors: SchemaValidationError[]
}

export interface SafeRemovalError {
    readonly namespace: string
    readonly dataType: string
    readonly invalidData: any
    readonly exception: Error
}

export interface SafeRemovalResult {
    readonly error: SafeRemovalError | null
    readonly result: Record<string, Record<string, any>> | null
}

export interface SchemaAPI {
    getNamespaceConfig(ns: string): any

    isSystemStyle(id: string): boolean

    getDefinition(componentType: string): any

    getSchema(namespace: string, schemaName: string): any

    hasSchemaForDataType(namespace: string, type: string): boolean

    isDraftDataSchema(namespace: string, type: string): boolean

    getReferencePointer(ownerPointer: Pointer, namespace: string, referenceName?: string): Pointer | null

    getReferencePath(ownerPointer: Pointer, namespace: string, referenceName?: string): readonly string[] | undefined

    createItemAccordingToSchema(type: string, namespace: string, overrides?: any): any

    getReferences(namespace: string, value: DalValue): readonly ResolvedReference[]

    validate(dataTypeName: string, data: any, namespace: string): void

    validateNonStrict(dataTypeName: string, data: any, namespace: string): void

    addDefaultsAndValidate(dataTypeName: string, data: any, namespace: string): void

    hasNamespace(namespace: string): boolean

    removeAdditionalProperties(namespace: string, value: DalValue): void

    removeWhitelistedProperties(namespace: string, value: DalValue, conservativeRemoval: boolean): void

    removeAdditionalPropertiesSafely(data: Record<string, Record<string, DalValue>>): SafeRemovalResult

    removeWhitelistedPropertiesSafely(
        data: Record<string, Record<string, DalValue>>,
        conservativeRemoval: boolean
    ): Record<string, Record<string, any>>

    extractReferenceFieldsInfo(namespace: string, dataTypeName: string, data?: any): readonly RefInfo[]

    extractOwnedReferenceFieldsInfo(
        namespace: string,
        dataTypeName: string,
        onlySerializable?: boolean,
        data?: any
    ): Record<string, RefInfo>

    addReferences(namespace: string, name: string, refs: RefInfo[]): void

    isSchemaCollectionRegistered(collectionName: string): boolean

    convertNamespaceFromServerStyle(name: string): string

    convertNamespaceToServerStyle(name: string): string

    registerComponentDefinitionAndSchemas(
        compType: string,
        {componentDefinition, dataSchemas, propertiesSchemas}: ComponentSchemasDefinition,
        allowOverrides?: boolean
    ): void

    registerSchema(namespace: string, schema: any): void

    addDataTypesToExistingNamespace(
        namespace: string,
        schemas: Record<string, SchemaFile>,
        override: boolean,
        shouldCombineAll: boolean
    ): void

    traverseReferences(
        dataItem: any,
        type: string,
        namespace: string,
        shouldTransform: (refTypes: readonly string[], value: any) => boolean,
        transformCallback: (value: any, refType?: readonly string[]) => any
    ): void
}

export type SchemaExtensionAPI = ExtensionAPI & {
    schemaAPI: SchemaAPI
}

const SINGLE_LAYOUT_DATA_TYPE = 'SingleLayoutData'

const REMOVAL_EXCLUSIONS = {
    removeInnerAdditionalProperties: {
        [SINGLE_LAYOUT_DATA_TYPE]: ['itemLayout', 'componentLayout', 'containerLayout']
    },
    additionalProperties: new Set([SINGLE_LAYOUT_DATA_TYPE]),
    whitelist: new Set([SINGLE_LAYOUT_DATA_TYPE])
}

const REMOVE_INNER_ADDITIONAL_PROPERTIES_TYPES = new Set(
    Object.keys(REMOVAL_EXCLUSIONS.removeInnerAdditionalProperties)
)

const createIsTypeInSetFunc =
    (set: Set<string>) =>
    (schema: any, namespace: string, value: DalValue): boolean => {
        const type = schema.getSchemaType(namespace, value)
        return set.has(type)
    }

const getRemoveWhitelistedPropertiesMethods = (schema: DalSchema, schemaService: any) => {
    const shouldExcludeFromRemoveWhitelistedProperties = createIsTypeInSetFunc(REMOVAL_EXCLUSIONS.whitelist)

    const removeWhitelistedProperties = (namespace: string, value: DalValue, conservativeRemoval: boolean) => {
        if (value?.type && schemaService.hasNamespace(namespace)) {
            if (shouldExcludeFromRemoveWhitelistedProperties(schema, namespace, value)) {
                return value
            }
            schemaService.whitelistCleanup.removeWhitelistedProperties(
                namespace,
                value.type,
                value,
                conservativeRemoval
            )
        }
    }

    return {
        removeWhitelistedProperties
    }
}

const createExtensionAPI = ({dal, coreConfig, extensionAPI}: CreateExtArgs): SchemaExtensionAPI => {
    const {schemaService} = coreConfig
    const {schema} = dal
    const {removeWhitelistedProperties} = getRemoveWhitelistedPropertiesMethods(schema, schemaService)
    const dataAccess = () => (extensionAPI as DataAccessExtensionApi).dataAccess.withSuper

    const shouldRemoveInnerAdditionalProperties = createIsTypeInSetFunc(REMOVE_INNER_ADDITIONAL_PROPERTIES_TYPES)

    const removeInnerAdditionalProperties = (namespace: string, value: DalValue): void => {
        const innerPropertiesKeys = REMOVAL_EXCLUSIONS.removeInnerAdditionalProperties[value.type]
        innerPropertiesKeys.forEach((key: string) => {
            const innerValue = value[key]
            if (innerValue?.type) {
                schemaService.removeAdditionalProperties(namespace, innerValue.type, innerValue)
                delete innerValue.metaData
            }
        })
    }

    const shouldExcludeFromRemoveAdditionalProperties = createIsTypeInSetFunc(REMOVAL_EXCLUSIONS.additionalProperties)

    const removeAdditionalProperties = (namespace: string, value: DalValue) => {
        if (value?.type && schemaService.hasNamespace(namespace)) {
            if (shouldRemoveInnerAdditionalProperties(schema, namespace, value)) {
                return removeInnerAdditionalProperties(namespace, value)
            }
            if (shouldExcludeFromRemoveAdditionalProperties(schema, namespace, value)) {
                return value
            }
            schemaService.removeAdditionalProperties(namespace, value.type, value)
        }
    }

    const {
        validateStrict,
        validateNoDefaults,
        hasNamespace,
        addDefaultsAndValidate,
        getDefinition,
        getSchema,
        hasSchemaForDataType,
        isDraftDataSchema,
        createItemAccordingToSchema,
        extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo,
        addReferences,
        isSchemaCollectionRegistered,
        registerComponentDefinitionAndSchemas,
        addDataTypesToExistingNamespace,
        traverseReferences
    } = schemaService

    const removeAdditionalPropertiesSafely = (data: Record<string, Record<string, DalValue>>): SafeRemovalResult => {
        const result = _.cloneDeep(data)
        for (const [namespace, v] of _.toPairs(result)) {
            for (const [id, value] of _.toPairs(v)) {
                try {
                    removeAdditionalProperties(namespace, value)
                } catch (e) {
                    return {
                        result: null,
                        error: {
                            namespace,
                            dataType: value.type,
                            exception: e as Error,
                            invalidData: data[namespace][id]
                        }
                    }
                }
            }
        }
        return {error: null, result}
    }

    const removeWhitelistedPropertiesSafely = (
        data: Record<string, Record<string, DalValue>>,
        conservativeRemoval: boolean
    ): Record<string, Record<string, any>> => {
        const result = _.cloneDeep(data)
        _.forEach(result, (namespaceData, namespace) => {
            _.forEach(namespaceData, value => {
                removeWhitelistedProperties(namespace, value, conservativeRemoval)
            })
        })
        return result
    }

    const getReferencePath = (
        ownerPointer: Pointer,
        namespace: string,
        referenceName?: string
    ): readonly string[] | undefined => {
        const dataItem = dataAccess().get(ownerPointer)

        if (referenceName) {
            return [referenceName]
        }

        if (_.includes(VIEW_MODES, ownerPointer.type)) {
            return [COMP_DATA_QUERY_KEYS_WITH_STYLE[namespace]]
        }

        const refs = dal.schema.getReferences(ownerPointer.type, dataItem) ?? []
        return _.find(refs, r => r?.referencedMap === namespace)?.refInfo.path
    }

    const getReferencePointer = (ownerPointer: Pointer, namespace: string, referenceName?: string): Pointer | null => {
        const dataItem = dataAccess().get(ownerPointer)
        const refPath = getReferencePath(ownerPointer, namespace, referenceName)
        const id = stripHashIfExists(_.get(dataItem, refPath!))
        if (!id) {
            return null
        }
        return pointerUtils.getPointer(id, namespace)
    }

    return {
        schemaAPI: {
            getNamespaceConfig: namespaceMapping.getNamespaceConfig,
            getReferencePath,
            getReferencePointer,
            hasSchemaForDataType,
            getSchema,
            getDefinition,
            isDraftDataSchema,
            createItemAccordingToSchema,
            isSystemStyle: schema.isSystemStyle,
            getReferences: schema.getReferences,
            validate: validateStrict,
            validateNonStrict: validateNoDefaults,
            addDefaultsAndValidate,
            hasNamespace,
            removeAdditionalProperties,
            removeWhitelistedProperties,
            removeAdditionalPropertiesSafely,
            removeWhitelistedPropertiesSafely,
            extractReferenceFieldsInfo,
            extractOwnedReferenceFieldsInfo,
            addReferences,
            isSchemaCollectionRegistered,
            registerComponentDefinitionAndSchemas,
            registerSchema(namespace: string, schemaObject: any) {
                schemaService.registerDataTypeSchema(schemaObject, namespace)
            },
            convertNamespaceFromServerStyle: namespaceMapping.convertNamespaceFromServerStyle,
            convertNamespaceToServerStyle: namespaceMapping.convertNamespaceToServerStyle,
            addDataTypesToExistingNamespace,
            traverseReferences
        }
    }
}

const getValidationErrorMessage = (err: ReportableError, dataId: string, dataNamespace: string): string => {
    let parsedMessage

    try {
        parsedMessage = JSON.parse(err.message)
    } catch (e) {
        parsedMessage = err.message
    }

    const validationErrorMessage: ParsedSchemaValidationErrorMessage = {
        offendedDataId: dataId,
        offendedDataNamespace: dataNamespace,
        errors: parsedMessage
    }

    return JSON.stringify(validationErrorMessage)
}

const buildValidationError = (validationResult: ReportableError, id: string, namespace: string) => [
    {
        shouldFail: true,
        type: validationResult.errorType,
        message: getValidationErrorMessage(validationResult, id, namespace),
        tags: validationResult.tags ?? {},
        extras: {
            ...(validationResult.extras ?? {}),
            error: _.pick(validationResult as ReportableError, ['errorType', 'message'])
        }
    }
]

const createValidator = ({dal, coreConfig}: DmApis) => {
    const {schema} = dal
    const {experimentInstance, schemaService} = coreConfig
    const {removeWhitelistedProperties} = getRemoveWhitelistedPropertiesMethods(schema, schemaService)

    const validateSchema = (pointer: Pointer, value: DalValue): ValidatorResult[] => {
        if (_.isNil(value)) {
            return []
        }

        const namespace = pointer.type
        if (!schema.hasNamespace(namespace)) {
            return []
        }

        if (
            !experimentInstance.isOpen('dm_noWhitelistDalStrictSchemaValidation') &&
            !coreConfig.enforceStrictValidationEverywhere
        ) {
            const isDataTypeWhitelisted = schemaService.whitelistCleanup.isDataTypeWhitelisted(
                namespace,
                value?.type,
                true
            )
            if (isDataTypeWhitelisted) {
                value = _.cloneDeep(value)
                removeWhitelistedProperties(namespace, value, true)
            }
        }

        const schemaType = schema.getSchemaType(namespace, value)

        const strictValidationError = _.attempt(
            schema.validateStrict,
            schemaType,
            value,
            namespace
        ) as void | ReportableError

        if (!strictValidationError) {
            return []
        }

        if (
            experimentInstance.isOpen('dm_failDalStrictSchemaValidation') ||
            coreConfig.enforceStrictValidationEverywhere
        ) {
            strictValidationError.tags = {...(strictValidationError.tags ?? {}), failedStrictValidation: true}
            return buildValidationError(strictValidationError, value.id, namespace)
        }

        const nonStrictValidationError = _.attempt(
            schema.validate,
            schemaType,
            value,
            namespace
        ) as void | ReportableError

        if (!nonStrictValidationError) {
            coreConfig.logger.captureError(
                new ReportableError({
                    errorType: 'strictValidationMismatch',
                    message: 'Validation mismatch between strict and non strict validation',
                    extras: {
                        strictValidationError,
                        nonStrictValidationError,
                        namespace,
                        schemaType,
                        id: value.id
                    }
                })
            )
            return []
        }

        nonStrictValidationError.tags = {...(nonStrictValidationError.tags ?? {}), failedNonStrictValidation: true}
        return buildValidationError(nonStrictValidationError, value.id, namespace)
    }

    const validateDalSetValue = (pointer: Pointer, setValue: DalValue) => {
        const isValueInvalid = (value: DalValue) => !(value.type && value.id)
        const shouldCheck = !namespacesWithoutTypeAndId.has(pointer.type)
        const hasProblem = shouldCheck && setValue && isValueInvalid(setValue)
        if (hasProblem) {
            return [
                {
                    shouldFail: true,
                    type: 'invalidDalSetValues',
                    message: `pointer of type ${pointer.type} is missing id or type`,
                    extras: {
                        id: pointer.id,
                        type: pointer.type,
                        setValue
                    }
                }
            ]
        }
    }

    return {
        validateSchema,
        validateDalSetValue
    }
}

const createExtension = (): Extension => ({
    name: 'schema',
    dependencies: new Set([]),
    createExtensionAPI,
    createValidator
})

export {createExtension}
